import React from 'react';
import { Task } from '../types/Project';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface TaskListProps {
  tasks: Task[];
  onEditTask: (task: Task) => void;
  onDeleteTask: (id: string) => void;
  onUpdateProgress: (id: string, progress: number) => void;
}

export const TaskList: React.FC<TaskListProps> = ({ 
  tasks, 
  onEditTask, 
  onDeleteTask,
  onUpdateProgress
}) => {
  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'not-started': return '#6b7280';
      case 'in-progress': return '#3b82f6';
      case 'completed': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: Task['status']) => {
    switch (status) {
      case 'not-started': return '未開始';
      case 'in-progress': return '進行中';
      case 'completed': return '完了';
      default: return '不明';
    }
  };

  const handleProgressChange = (taskId: string, newProgress: number) => {
    onUpdateProgress(taskId, newProgress);
  };

  if (tasks.length === 0) {
    return (
      <div className="task-list-empty">
        <p>まだタスクがありません。新しいタスクを作成してください。</p>
      </div>
    );
  }

  return (
    <div className="task-list">
      <div className="task-header">
        <h3>タスク一覧 ({tasks.length}件)</h3>
      </div>
      
      <div className="task-table">
        <div className="task-table-header">
          <div className="task-col-name">タスク名</div>
          <div className="task-col-assignee">担当者</div>
          <div className="task-col-status">ステータス</div>
          <div className="task-col-progress">進捗</div>
          <div className="task-col-dates">期間</div>
          <div className="task-col-actions">操作</div>
        </div>
        
        {tasks.map(task => (
          <div key={task.id} className="task-row">
            <div className="task-col-name">
              <div className="task-name">{task.name}</div>
              {task.description && (
                <div className="task-description">{task.description}</div>
              )}
              {task.dependencies && task.dependencies.length > 0 && (
                <div className="task-dependencies">
                  <small>依存: {task.dependencies.length}件</small>
                </div>
              )}
            </div>
            
            <div className="task-col-assignee">
              {task.assignee || '未割当'}
            </div>
            
            <div className="task-col-status">
              <span 
                className="status-badge" 
                style={{ backgroundColor: getStatusColor(task.status) }}
              >
                {getStatusText(task.status)}
              </span>
            </div>
            
            <div className="task-col-progress">
              <div className="progress-container">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={task.progress}
                  onChange={(e) => handleProgressChange(task.id, parseInt(e.target.value))}
                  className="progress-slider"
                />
                <span className="progress-value">{task.progress}%</span>
              </div>
            </div>
            
            <div className="task-col-dates">
              <div className="date-range">
                <div>{format(task.startDate, 'MM/dd', { locale: ja })}</div>
                <div>〜</div>
                <div>{format(task.endDate, 'MM/dd', { locale: ja })}</div>
              </div>
            </div>
            
            <div className="task-col-actions">
              <button 
                className="btn-edit"
                onClick={() => onEditTask(task)}
                title="編集"
              >
                ✏️
              </button>
              <button 
                className="btn-delete"
                onClick={() => onDeleteTask(task.id)}
                title="削除"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
